import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Card, Badge, EmptyState, fmtMoney } from "@/components/ui-kit";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar } from "recharts";

export default function CashFlow() {
  const { t } = useLang();
  const [entries, setEntries] = useState([]);
  const load = async () => { const { data } = await api.get("/cashflow"); setEntries(data); };
  useEffect(() => { load(); }, []);

  const inflow = entries.filter(e => e.flow_type === "inflow").reduce((s, e) => s + e.amount, 0);
  const outflow = entries.filter(e => e.flow_type === "outflow").reduce((s, e) => s + e.amount, 0);
  const net = inflow - outflow;

  // Group by date
  const byDate = {};
  entries.forEach(e => {
    const d = (e.date || e.created_at || "").slice(0, 10);
    if (!byDate[d]) byDate[d] = { date: d, inflow: 0, outflow: 0 };
    byDate[d][e.flow_type] += e.amount;
  });
  const series = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);

  return (
    <div data-testid="cashflow-page">
      <PageHeader title={t("cashflow")} subtitle="Cash inflows, outflows & balance" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><div className="overline">Inflow</div><div className="metric-value text-emerald-600 mt-1">{fmtMoney(inflow)} EGP</div></Card>
        <Card className="p-5"><div className="overline">Outflow</div><div className="metric-value text-red-600 mt-1">{fmtMoney(outflow)} EGP</div></Card>
        <Card className="p-5"><div className="overline">Net Balance</div><div className={`metric-value mt-1 ${net >= 0 ? "text-blue-700" : "text-red-700"}`}>{fmtMoney(net)} EGP</div></Card>
      </div>

      <Card className="p-5 mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Daily Cash Flow</h3>
        {series.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
              <XAxis dataKey="date" stroke="#64748b" fontSize={10}/>
              <YAxis stroke="#64748b" fontSize={10}/>
              <Tooltip/>
              <Legend/>
              <Bar dataKey="inflow" fill="#10b981" />
              <Bar dataKey="outflow" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No cash flow yet"/>}
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">Date</th>
                <th className="py-2 px-3 text-start">Type</th>
                <th className="py-2 px-3 text-start">Category</th>
                <th className="py-2 px-3 text-start">Description</th>
                <th className="py-2 px-3 text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 50).map(e => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-xs">{(e.date || e.created_at || "").slice(0, 10)}</td>
                  <td className="py-2 px-3"><Badge color={e.flow_type === "inflow" ? "green" : "red"}>{e.flow_type}</Badge></td>
                  <td className="py-2 px-3">{e.category}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{e.description || "-"}</td>
                  <td className={`py-2 px-3 text-end font-bold ${e.flow_type === "inflow" ? "text-emerald-700" : "text-red-700"}`}>{fmtMoney(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>
    </div>
  );
}
